import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Brain, Workflow, HardDrive } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { DEV_MODE } from "@/lib/dev-mode";
import { formatBytes } from "@/lib/format";

interface QuotaData {
  ai_calls_today: number;
  ai_calls_limit: number;
  workflow_runs_today: number;
  workflow_runs_limit: number;
  storage_bytes_used: number;
  storage_bytes_limit: number;
}


export const UsageQuotaWidget = () => {
  const [quota, setQuota] = useState<QuotaData | null>(null);

  useEffect(() => {
    fetchQuota();
  }, []);

  const fetchQuota = async () => {
    if (DEV_MODE) {
      setQuota({
        ai_calls_today: 12,
        ai_calls_limit: 50,
        workflow_runs_today: 3,
        workflow_runs_limit: 10,
        storage_bytes_used: 256 * 1024 * 1024,
        storage_bytes_limit: 1073741824,
      });
      return;
    }

    try {
      const { data } = await supabase
        .from("usage_quotas")
        .select("*")
        .maybeSingle();

      if (data) setQuota(data as QuotaData);
    } catch (err) {
      console.error("Failed to fetch quota:", err);
    }
  };

  if (!quota) return null;

  const items = [
    {
      label: "AI Calls",
      icon: Brain,
      used: quota.ai_calls_today,
      limit: quota.ai_calls_limit,
      display: `${quota.ai_calls_today} / ${quota.ai_calls_limit}`,
    },
    {
      label: "Workflows",
      icon: Workflow,
      used: quota.workflow_runs_today,
      limit: quota.workflow_runs_limit,
      display: `${quota.workflow_runs_today} / ${quota.workflow_runs_limit}`,
    },
    {
      label: "Storage",
      icon: HardDrive,
      used: quota.storage_bytes_used,
      limit: quota.storage_bytes_limit,
      display: `${formatBytes(quota.storage_bytes_used)} / ${formatBytes(quota.storage_bytes_limit)}`,
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Usage Today</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((item) => {
          const pct = item.limit > 0 ? (item.used / item.limit) * 100 : 0;
          const isWarning = pct >= 80;
          return (
            <div key={item.label} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-foreground">
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <span>{item.label}</span>
                </div>
                <span className={`text-xs ${isWarning ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                  {item.display}
                </span>
              </div>
              <Progress
                value={Math.min(pct, 100)}
                className={`h-2 ${isWarning ? "[&>div]:bg-destructive" : ""}`}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
